import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react'
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import React, { useId } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { Spinner } from '../spinner.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './split-button.css.ts'

export type SplitButtonItem<TValue extends string> = {
  value: TValue
  label: string
  description?: string
}

export const SplitButtonTypeSchema = z.enum(['primary', 'secondary'])
export type SplitButtonType = z.infer<typeof SplitButtonTypeSchema>

export const SplitButtonSizeSchema = z.enum(['default', 'small', 'compact'])
export type SplitButtonSize = z.infer<typeof SplitButtonSizeSchema>

export const SplitButtonStateSchema = z.enum(['default', 'disabled', 'loading'])
export type SplitButtonState = z.infer<typeof SplitButtonStateSchema>

export type SplitButtonProps<TValue extends string> = {
  /** What the main half does when pressed. */
  children: React.ReactNode
  onClick: () => unknown
  /** The heading above the alternatives, e.g. "SCAN EFFORT". */
  menuLabel: string
  /** The caret's accessible name, e.g. "Choose scan effort". */
  menuTriggerLabel: string
  items: readonly SplitButtonItem<TValue>[]
  /** The item the main half stands for; it carries the check mark. */
  defaultValue: TValue
  onSelect: (value: TValue) => unknown
  state?: SplitButtonState
  /** Why the button is inert, shown on hover and focus. Without it a dead control is just a broken one. */
  disabledReason?: string
  /** Disables only the caret, for why; the main half stays usable. */
  menuDisabledReason?: string
  buttonType?: SplitButtonType
  size?: SplitButtonSize
  loadingLabel?: string
}

/**
 * An action with alternatives behind a caret: the main half runs the default, the menu runs a
 * variant of it. Picking a menu item acts at once — the menu is a choice of how, not a setting.
 */
export const SplitButton = <TValue extends string>({
  children,
  onClick,
  menuLabel,
  menuTriggerLabel,
  items,
  defaultValue,
  onSelect,
  state = 'default',
  disabledReason,
  menuDisabledReason,
  buttonType = 'primary',
  size = 'default',
  loadingLabel = 'Starting',
}: SplitButtonProps<TValue>) => {
  const reasonId = useId()
  const isInert = state !== 'default'
  const reason = match(state)
    .with('disabled', () => disabledReason)
    .with('default', () => menuDisabledReason)
    .with('loading', () => undefined)
    .exhaustive()

  const group = (
    <div
      className={styles.group({ buttonType, size, isInert })}
      role="group"
      aria-describedby={reason ? reasonId : undefined}
      // Focusable while disabled so a keyboard user can reach the reason in the tooltip.
      tabIndex={state === 'disabled' && disabledReason ? 0 : undefined}
    >
      <button
        type="button"
        className={styles.main({ buttonType, size })}
        onClick={() => onClick()}
        disabled={isInert}
        aria-busy={state === 'loading'}
      >
        {state === 'loading' ? <Spinner label={loadingLabel} size="auto" /> : children}
      </button>
      <RadixDropdownMenu.Root>
        <RadixDropdownMenu.Trigger
          className={styles.caret({ buttonType, size })}
          aria-label={menuTriggerLabel}
          disabled={isInert || !!reason}
        >
          <CaretDownIcon className={styles.caretIcon} size={size === 'compact' ? 14 : 16} weight="fill" />
        </RadixDropdownMenu.Trigger>
        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.Content className={styles.menu} align="end" sideOffset={6}>
            <RadixDropdownMenu.Label className={styles.menuLabel}>{menuLabel}</RadixDropdownMenu.Label>
            {items.map(item => (
              <RadixDropdownMenu.Item
                key={item.value}
                className={styles.menuItem}
                onSelect={() => onSelect(item.value)}
              >
                <span className={styles.menuItemText}>
                  <span className={styles.menuItemLabel}>{item.label}</span>
                  {item.description && <span className={styles.menuItemDescription}>{item.description}</span>}
                </span>
                <span className={styles.menuItemCheck} aria-hidden>
                  {item.value === defaultValue && <CheckIcon size={14} weight="bold" />}
                </span>
              </RadixDropdownMenu.Item>
            ))}
          </RadixDropdownMenu.Content>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Root>
      {reason && (
        <span id={reasonId} className={styles.visuallyHidden}>
          {reason}
        </span>
      )}
    </div>
  )

  return reason ? (
    <TooltipWithHover trigger={group} side="bottom" size="small" maxWidth={260}>
      {reason}
    </TooltipWithHover>
  ) : (
    group
  )
}
